import { BUNDLED_FONT_ASSET_URLS } from './assets.generated.ts';
import { FontLibraryError } from './errors.ts';
import { V46_FONT_PROFILES } from './manifest.generated.ts';
import type {
  BrowserFontEnvironment,
  BrowserReadyFontProfile,
  FontByteSource,
  ResolvedFontBinary,
  V46FontProfile,
} from './types.ts';

const profilesByFamily: ReadonlyMap<string, V46FontProfile> = new Map(
  V46_FONT_PROFILES.map((profile) => [profile.family, profile] as const),
);
const profilesByAssetId: ReadonlyMap<string, V46FontProfile> = new Map(
  V46_FONT_PROFILES.map((profile) => [profile.fontAssetId, profile] as const),
);

function bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function sha256(bytes: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new FontLibraryError(
      'FONT_BROWSER_API_UNAVAILABLE',
      'No existe crypto.subtle para verificar la integridad de la fuente.',
    );
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytesToArrayBuffer(bytes));
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

export function getV46FontProfile(family: string): V46FontProfile {
  const profile = profilesByFamily.get(family);
  if (!profile) {
    throw new FontLibraryError('FONT_UNKNOWN', 'La familia no pertenece al contrato tipografico V4.6.', {
      family,
    });
  }
  return profile;
}

export function getV46FontProfileByAssetId(fontAssetId: string): V46FontProfile {
  const profile = profilesByAssetId.get(fontAssetId);
  if (!profile) {
    throw new FontLibraryError('FONT_UNKNOWN', 'El activo tipografico no pertenece al contrato V4.6.', {
      fontAssetId,
    });
  }
  return profile;
}

export function fontCssShorthand(profile: V46FontProfile, sizePx: number): string {
  if (!Number.isFinite(sizePx) || sizePx <= 0) {
    throw new FontLibraryError('FONT_NOT_READY', 'El tamano de comprobacion debe ser positivo.', {
      sizePx,
    });
  }
  const family = profile.family.replaceAll('\\', '\\\\').replaceAll('"', '\\"');
  return `${profile.style} ${profile.weight} ${sizePx}px "${family}"`;
}

export class V46FontLibrary {
  private readonly byteSource: FontByteSource;
  private readonly verifiedBytes = new Map<string, Uint8Array>();
  private readonly installedBrowserFaces = new Set<string>();

  constructor(byteSource: FontByteSource) {
    this.byteSource = byteSource;
  }

  profile(family: string): V46FontProfile {
    return getV46FontProfile(family);
  }

  async resolve(fontAssetId: string): Promise<ResolvedFontBinary | null> {
    const profile = profilesByAssetId.get(fontAssetId);
    if (!profile) return null;
    const bytes = await this.loadVerified(profile);
    return {
      fontAssetId,
      family: profile.family,
      weight: profile.weight,
      style: profile.style,
      format: profile.format,
      bytes,
      sha256: profile.sha256,
    };
  }

  async ensureBrowserReady(
    family: string,
    environment: BrowserFontEnvironment,
    options: { readonly sizePx?: number; readonly sampleText?: string } = {},
  ): Promise<BrowserReadyFontProfile> {
    const profile = getV46FontProfile(family);
    const sampleText = options.sampleText ?? 'Tu marca';
    const shorthand = fontCssShorthand(profile, options.sizePx ?? 80);
    if (!environment?.fonts || typeof environment.createFontFace !== 'function') {
      throw new FontLibraryError(
        'FONT_BROWSER_API_UNAVAILABLE',
        'El navegador no ofrece FontFace y document.fonts para cargar la tipografia local.',
        { family },
      );
    }
    if (!this.installedBrowserFaces.has(profile.fontAssetId)) {
      const bytes = await this.loadVerified(profile);
      const face = environment.createFontFace(profile.family, bytesToArrayBuffer(bytes), {
        weight: String(profile.weight),
        style: profile.style,
        display: 'block',
      });
      const loadedFace = await face.load();
      environment.fonts.add(loadedFace);
      this.installedBrowserFaces.add(profile.fontAssetId);
    }
    await environment.fonts.load(shorthand, sampleText);
    await environment.fonts.ready;
    if (!environment.fonts.check(shorthand, sampleText)) {
      throw new FontLibraryError(
        'FONT_NOT_READY',
        'La fuente local no quedo lista; se bloqueo el texto para evitar fallback silencioso.',
        { family, shorthand },
      );
    }
    return {
      family: profile.family,
      weight: profile.weight,
      style: profile.style,
      fontAssetId: profile.fontAssetId,
    };
  }

  private async loadVerified(profile: V46FontProfile): Promise<Uint8Array> {
    const cached = this.verifiedBytes.get(profile.fontAssetId);
    if (cached) return cached;
    let bytes: Uint8Array;
    try {
      bytes = await this.byteSource.load(profile);
    } catch (cause) {
      throw new FontLibraryError(
        'FONT_BYTES_MISSING',
        'No se pudieron cargar los bytes locales de la fuente.',
        { family: profile.family, assetFile: profile.assetFile },
        { cause },
      );
    }
    if (!(bytes instanceof Uint8Array) || bytes.byteLength !== profile.byteLength) {
      throw new FontLibraryError(
        'FONT_BYTES_INVALID',
        'El archivo tipografico local tiene un tamano inesperado.',
        { family: profile.family, expected: profile.byteLength, actual: bytes?.byteLength },
      );
    }
    const actualHash = await sha256(bytes);
    if (actualHash !== profile.sha256) {
      throw new FontLibraryError(
        'FONT_HASH_MISMATCH',
        'El archivo tipografico local no coincide con el manifiesto.',
        { family: profile.family, expected: profile.sha256, actual: actualHash },
      );
    }
    this.verifiedBytes.set(profile.fontAssetId, bytes);
    return bytes;
  }
}

export function createBundledFontByteSource(
  fetcher: typeof fetch = globalThis.fetch,
): FontByteSource {
  return {
    async load(profile) {
      const url = BUNDLED_FONT_ASSET_URLS[
        profile.fontAssetId as keyof typeof BUNDLED_FONT_ASSET_URLS
      ];
      if (!url || typeof fetcher !== 'function') {
        throw new Error(`No existe URL local para ${profile.fontAssetId}.`);
      }
      const response = await fetcher(url);
      if (!response.ok) throw new Error(`HTTP ${response.status} al cargar ${url.href}`);
      return new Uint8Array(await response.arrayBuffer());
    },
  };
}

export function createDefaultBrowserFontEnvironment(): BrowserFontEnvironment {
  if (typeof document === 'undefined' || typeof FontFace === 'undefined') {
    throw new FontLibraryError(
      'FONT_BROWSER_API_UNAVAILABLE',
      'FontFace y document.fonts solo estan disponibles en un navegador compatible.',
    );
  }
  return {
    fonts: document.fonts as unknown as BrowserFontEnvironment['fonts'],
    createFontFace: (family, source, descriptors) =>
      new FontFace(family, source, descriptors) as unknown as ReturnType<
        BrowserFontEnvironment['createFontFace']
      >,
  };
}

export function bindV46FontLibraryToBrowser(
  library: V46FontLibrary,
  environment: BrowserFontEnvironment,
): {
  ensureReady(
    family: string,
    options: { readonly sizePx: number; readonly sampleText: string },
  ): Promise<BrowserReadyFontProfile>;
} {
  return {
    ensureReady: (family, options) => library.ensureBrowserReady(family, environment, options),
  };
}
